import { XCircle, Plus } from "lucide-react";
import { PageLoader } from "../components/loaders/pageloader"
import { ProjectSection, WorkExperienceSection } from "../components/bragsheet_sections"
import { useBragsheet } from "../context/bragsheet-context";


export const BragSheetView = () => {

    const { summary, workExperience, projects, skills, 
            updateSummary, updateWorkExperience, updateProject, addSkill, removeSkill,
            addWorkExperience, addProject,
            saveBragsheet,
            loading, error } = useBragsheet()
    

    if(loading){
        return <PageLoader />
    }

    return (
        <section className="bg-gradient-to-b from-white to-indigo-50 sm:px-4 px-2 py-20">
            {error && <h1>{error}</h1>}
            <div className="container fade-in max-w-6xl mx-auto">
                <div className="flex items-end justify-between gap-4 mb-8">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900">My Brag Sheet</h2>
                        <p className="text-gray-600 mt-2">Keep your highlights in one place. Used to craft focused resume sections.</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-3">
                        <button className="bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg shadow-sm transition">Preview</button>
                        <button onClick={saveBragsheet}
                        className="bg-indigo-600 text-white font-semibold px-5 py-2 rounded-lg shadow-md hover:bg-indigo-700 transition">Save</button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        {/* Professional Summary */}
                        <div className="bg-white/80 backdrop-blur rounded-2xl border border-gray-200 shadow-sm sm:p-6 p-2">
                            <label htmlFor="summary" className="block text-base font-semibold text-gray-800 mb-2">Professional Summary</label>
                            <textarea id="summary" rows="4" className="w-full p-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition" value={summary} onChange={(e) => updateSummary(e.target.value)}></textarea>
                        </div>

                        {/* Work Experience */}
                        <div className="bg-white/80 backdrop-blur rounded-2xl border border-gray-200 shadow-sm sm:p-6 p-2">
                            <div className="flex items-center justify-between my-4">
                                <h3 className="text-base font-semibold text-gray-800">Work Experience</h3>
                                <button 
                                    onClick={addWorkExperience}
                                    className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
                                >
                                    <Plus size={16} /> Add Experience
                                </button>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {workExperience.map(exp => (
                                    <WorkExperienceSection 
                                        key={exp.id}
                                        exp={exp} 
                                        onChange={updateWorkExperience}
                                    />
                                ))}
                            </div>
                        </div>
                        {/* Projects */}
                        <div className="bg-white/80 backdrop-blur rounded-2xl border border-gray-200 shadow-sm sm:p-6 p-2">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-semibold text-gray-800">Projects</h3>
                                <button 
                                    onClick={addProject}
                                    className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
                                >
                                    <Plus size={16} /> Add Project
                                </button>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {projects.map(project => (
                                    <ProjectSection 
                                        key={project.id}
                                        project={project}
                                        onChange={updateProject}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* Skills */}
                        <div className="bg-white/80 backdrop-blur rounded-2xl border border-gray-200 shadow-sm sm:p-6 p-2">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-semibold text-gray-800">Skills</h3>
                                <input 
                                    placeholder="Add a skill" 
                                    className="hidden sm:block px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition" 
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            addSkill(e.target.value)
                                            e.target.value = '';
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {skills?.map(skill => (
                                    <span key={skill} className="group bg-gray-100 text-gray-800 px-3 py-1 rounded-full inline-flex items-center hover:bg-gray-200 transition">
                                        {skill}
                                        <XCircle 
                                            size={14} 
                                            className="ml-2 cursor-pointer text-gray-400 group-hover:text-gray-500"
                                            onClick={() => removeSkill(skill)}
                                        />
                                    </span>
                                )) || []}
                                <input 
                                    placeholder="+ Add Skill" 
                                    className="sm:hidden bg-transparent p-1 py-2 border-b border-dashed border-gray-300 focus:outline-none"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            addSkill(e.target.value);
                                            e.target.value = '';
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sm:hidden sticky bottom-6 flex justify-center mt-8">
                    <button className="bg-indigo-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:bg-indigo-700">Save</button>
                </div>
            </div>
        </section>
    )
};
